import { PropTypes } from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import ButtonWithLoader from './ButtonWithLoader';

/**
 * TaskModal component to display a modal with a title, children, and buttons to submit or cancel.
 *
 * @param {*} props
 * @return {*} JSX.Element
 */
const TaskModal = (props) => {
  const {
    children,
    title,
    isNew,
    isOpen,
    close,
    onReady,
    onReset,
    className,
    disabled,
    showFooter,
    onReadyParams,
    isLoading,
    isSubmitting,
  } = props;

  const submitting = isSubmitting ?? isLoading;

  const intl = useIntl();
  const deleteMessage = isNew
    ? intl.formatMessage({ id: 'clearData' })
    : intl.formatMessage({ id: 'cancel' });
  const submitMessage = isNew
    ? intl.formatMessage({ id: 'addNewTask' })
    : intl.formatMessage({ id: 'saveTask' });

  const onReadyWithParams = () => {
    onReady(...onReadyParams);
  };
  const buttonsDisabled = disabled || isLoading || submitting;

  const SubmitButton = ({ onClick }) =>
    isLoading ? (
      <ButtonWithLoader
        handleClick={onClick}
        isLoading={isLoading}
        style="submit-button confirm btn btn-primary"
      >
        <>{submitMessage}</>
      </ButtonWithLoader>
    ) : (
      <Button
        className="submit-button confirm"
        color="primary"
        onClick={onClick}
        disabled={buttonsDisabled}
      >
        {submitMessage}
      </Button>
    );

  return (
    <Modal isOpen={isOpen} toggle={close} className={className}>
      <ModalHeader className="modal-title" toggle={close}>
        {title}
      </ModalHeader>
      <ModalBody data-testid="modal">{children}</ModalBody>
      {showFooter && (
        <ModalFooter>
          <Button
            className="submit-button delete"
            color="secondary"
            onClick={onReset}
            disabled={buttonsDisabled}
          >
            <i className="icon icon-trash"></i> {deleteMessage}
          </Button>
          {onReady && !onReadyParams ? (
            <ButtonWithLoader
              style="submit-button confirm"
              handleClick={onReady}
              disabled={disabled}
              isLoading={submitting}
              options={{ height: '28px', width: '28px' }}
            >
              <span>{submitMessage}</span>
            </ButtonWithLoader>
          ) : onReady && onReadyParams ? (
            <ButtonWithLoader
              style="submit-button confirm"
              handleClick={onReadyWithParams}
              disabled={disabled}
              isLoading={submitting}
              options={{ height: '28px', width: '28px' }}
            >
              <span>{submitMessage}</span>
            </ButtonWithLoader>
          ) : null}
        </ModalFooter>
      )}
    </Modal>
  );
};

TaskModal.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node,
  title: PropTypes.string.isRequired,
  onReady: PropTypes.func,
  onReset: PropTypes.func,
  close: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isNew: PropTypes.bool,
  disabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  showFooter: PropTypes.bool,
  onReadyParams: PropTypes.arrayOf(PropTypes.any),
  isLoading: PropTypes.bool,
};

TaskModal.defaultProps = {
  showFooter: true,
};

export default TaskModal;
